import styled from '@emotion/native';
import React from 'react';
import { Link } from '../StyledText';
import { accent, green, sand, white } from '@/shared/styles/colors';
import { Pressable, TouchableOpacity } from 'react-native';
import useIsDesktop from '@/shared/hooks/useIsDesktop';
import { IsDesktopProps } from '@/shared/styles/types';
import { RelativePathString, usePathname } from 'expo-router';
import { ROUTES } from '@/shared/constants/routes';
import { IS_WEB } from '@/shared/constants/utility';
import { useWebHoverToggle } from '@/shared/hooks/useWebHoverToggle';
import { FONT_STYLES } from '@/shared/styles/constants';

interface Props {
  onPress: () => void;
  authText: string;
  showDarkColors?: boolean;
  progress?: number;
  setProgress?: (progress: number) => void;
  hideBackButton?: boolean;
  onLogoPress?: () => void;
}

const OnboardingHeader = ({
  onPress,
  authText,
  showDarkColors = false,
  progress = 0,
  setProgress,
  hideBackButton = false,
  onLogoPress,
}: Props) => {
  const isDesktop = useIsDesktop();
  const pathname = usePathname();
  const { isHovered: isAuthHovered, webHoverProps: authWebHoverProps } =
    useWebHoverToggle();

  // Hide back button on first two onboarding steps
  const showBackButton = progress > 1 && !hideBackButton;
  const showLogo = !showBackButton || hideBackButton || isDesktop;
  const trimmedPath = pathname.replace(/\/+$/, '');
  const normalizedPath = trimmedPath.length === 0 ? '/' : trimmedPath;
  const showBorderByPathname = [
    ROUTES.WELCOME.path,
    ROUTES.WELCOME_INTRO('stylist').path,
    ROUTES.WELCOME_INTRO('client').path,
  ].includes(normalizedPath as RelativePathString);
  const isWelcomeMarketingHome = normalizedPath === ROUTES.WELCOME.path;

  return (
    <Container
      showBorder={showBorderByPathname || isDesktop}
      isDesktop={isDesktop}
    >
      {showBackButton && (
        <TouchableOpacity
          onPress={() => setProgress && setProgress(progress - 1)}
        >
          <BackButton source={require('@/assets/images/BackWithCircle.png')} />
        </TouchableOpacity>
      )}
      {showLogo && (
        <Pressable onPress={onLogoPress}>
          <StyledImage
            isDesktop={isDesktop}
            source={
              showDarkColors
                ? require('@/assets/images/EmmeLogoDark.png')
                : require('@/assets/images/EmmeLogo.png')
            }
          />
        </Pressable>
      )}
      {!showLogo && !showBackButton && <PlaceholderView />}
      <HeaderText
        showDarkColors={showDarkColors}
        useWelcomeHelveticaNeue={isWelcomeMarketingHome}
        onPress={onPress}
        {...authWebHoverProps}
        style={
          IS_WEB && isAuthHovered
            ? { color: showDarkColors ? sand : white }
            : undefined
        }
      >
        {authText}
      </HeaderText>
    </Container>
  );
};

export default OnboardingHeader;

const Container = styled.View<{ showBorder: boolean; isDesktop: boolean }>(
  ({ showBorder, isDesktop }) => ({
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: isDesktop ? 52 : 24,
    paddingTop: IS_WEB ? 10 : 0,
    paddingBottom: 10,
    width: '100%',
    borderBottomColor: showBorder ? accent : 'transparent',
    borderBottomWidth: 1,
  }),
);

const StyledImage = styled.Image<IsDesktopProps>(({ isDesktop }) => ({
  width: isDesktop ? 88 : 73,
  height: 32,
  resizeMode: 'contain',
}));

const BackButton = styled.Image({
  width: 32,
  height: 32,
});

const HeaderText = styled(Link)(
  ({
    showDarkColors,
    useWelcomeHelveticaNeue,
  }: {
    showDarkColors: boolean;
    useWelcomeHelveticaNeue?: boolean;
  }) => ({
    color: showDarkColors ? green : sand,
    ...(useWelcomeHelveticaNeue ? FONT_STYLES.welcomeHelveticaNeue : {}),
  }),
);

const PlaceholderView = styled.View({
  width: 24,
});
